import { format } from '@monofront/business/utils';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useCartMaxWeight } from '@hooks/useCartMaxWeight';
import cn from 'classnames';

interface IStickyCTAProps {
  totalPrice: number;
  totalEconomy: number;
  weight: number;
  children: ReactNode;
  withWeightLimitMessage?: boolean;
  className?: string;
}

const StickyCTA: React.FC<IStickyCTAProps> = ({
  totalPrice,
  totalEconomy,
  weight,
  children,
  withWeightLimitMessage = false,
  className,
}) => {
  const tCart = useTranslations('Cart');
  const maxWeight = useCartMaxWeight();

  return (
    <>
      <div className="container">
        {withWeightLimitMessage && weight >= maxWeight && (
          <div className="max-weight-error">
            <p className="error tw-lf-error-message">
              {tCart('weightLimitWarning', { maxWeight })}
            </p>
          </div>
        )}
        <div className={cn('price-and-cta', className)}>
          <div className="price">
            <div className="total-price">{format(totalPrice)}</div>
            <div className="crossed-out-price">
              {format(totalPrice + totalEconomy)}
            </div>
          </div>
          <div className="cta tw-flex tw-items-center tw-justify-end">
            {children}
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          justify-content: space-between;
          padding: var(--space-4) var(--space-4);
          background-color: var(--white);
          bottom: env(safe-area-inset-bottom);
          position: absolute;
          height: 75px;
          right: 0;
          left: 0;
          border-top: 1px solid var(--clr-tertiary-2);
        }

        .price-and-cta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          gap: var(--space-3);
        }

        .total-price {
          font-size: var(--font-size-3);
          font-weight: var(--font-weight-bold);
          font-family: var(--font-family-1);
        }

        .crossed-out-price {
          text-align: center;
          text-decoration: line-through;
        }

        .cta {
          width: 100%;
        }

        .cta :global(button) {
          font-size: var(--font-size-2);
        }

        .cta > :global(.start-trial > button) {
          font-size: var(--font-size-2);
        }

        .max-weight-error {
          padding-bottom: var(--space-3);
          background-color: var(--clr-primary-5);
          text-align: center;
        }

        @media screen and (max-width: 800px) {
          .container {
            position: fixed;
            z-index: 1;
          }
        }

        @media screen and (max-width: 360px) {
          .cta {
            margin-left: var(--space-3);
          }
          .total-price {
            font-size: var(--font-size-3);
          }
        }

        @media screen and (max-width: 340px) {
          .cta {
            margin-left: 0;
          }
        }

        @media screen and (min-width: 800px) {
          .container {
            bottom: 0;
          }
        }
      `}</style>
    </>
  );
};

export default StickyCTA;
